import { ParseUtil } from '@reown/appkit-common';
import { mainnet, solana } from '@reown/appkit/networks';
import * as chains from '@reown/appkit/networks';
import { extractAddressFromRequest } from '../utils/RequestUtils';
// Manages wallet account state, provider access, and blockchain interactions
export class AccountController {
    appKit;
    constructor(appKit) {
        this.appKit = appKit;
    }
    async getProvider() {
        const provider = this.appKit?.getProvider('eip155');
        if (provider) {
            return provider;
        }
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('Timeout waiting for provider'));
            }, 10000);
            const unsubscribe = this.appKit?.subscribeProviders(state => {
                const provider = state.eip155;
                if (provider) {
                    clearTimeout(timeout);
                    unsubscribe?.();
                    resolve(provider);
                }
            });
        });
    }
    getAccount() {
        const eip155Account = this.appKit?.getAccount('eip155');
        const solanaAccount = this.appKit?.getAccount('solana');
        return {
            eip155: eip155Account,
            solana: solanaAccount
            // embeddedWalletInfo: eip155Account?.embeddedWalletInfo // should be the same for all namespaces, so we can use eip155
        };
    }
    async waitForProvider(appkit = this.appKit) {
        return new Promise((resolve, reject) => {
            const account = this.getAccount();
            if (account?.eip155?.status === 'disconnected' ||
                account?.solana?.status === 'disconnected') {
                reject(new Error('User is disconnected'));
                return;
            }
            const timeout = setTimeout(() => {
                reject(new Error('Timeout waiting for provider'));
            }, 10000);
            const unsubscribe = appkit.subscribeProviders(state => {
                if (state['eip155'] && state['solana']) {
                    clearTimeout(timeout);
                    unsubscribe();
                    resolve(state['eip155']);
                }
            });
        });
    }
    async getAccountStatus(timeoutMs = 15000, accountTypes = ['eip155', 'solana']) {
        // Early return if all namespaces are already disconnected
        const allAccountStatuses = accountTypes.map(accountType => {
            const status = this.appKit?.getAccount(accountType)?.status;
            return { accountType, status };
        });
        const allDisconnected = allAccountStatuses.every(({ status }) => status === 'disconnected');
        if (allDisconnected) {
            return 'disconnected';
        }
        const startTime = Date.now();
        await this.appKit?.switchNetwork(mainnet);
        const eip155Status = await this.waitForSingleAccountConnection('eip155', timeoutMs - (Date.now() - startTime));
        if (eip155Status === 'disconnected') {
            return 'disconnected';
        }
        await this.appKit?.switchNetwork(solana);
        const remainingTimeout = timeoutMs - (Date.now() - startTime);
        if (remainingTimeout <= 0) {
            return 'disconnected';
        }
        const solanaStatus = await this.waitForSingleAccountConnection('solana', remainingTimeout);
        return solanaStatus;
    }
    async waitForSingleAccountConnection(accountType, timeoutMs) {
        return new Promise(resolve => {
            const startTime = Date.now();
            const pollInterval = 200; // Poll every 200ms
            const poll = () => {
                // Check if timeout has been reached
                if (Date.now() - startTime >= timeoutMs) {
                    resolve('disconnected');
                    return;
                }
                const status = this.appKit?.getAccount(accountType)?.status;
                // If account status is undefined, continue polling
                if (!status) {
                    setTimeout(poll, pollInterval);
                    return;
                }
                // If account is disconnected, immediately return disconnected
                if (status === 'disconnected') {
                    resolve('disconnected');
                    return;
                }
                // If account is connected, return connected
                if (status === 'connected') {
                    resolve('connected');
                    return;
                }
                // If account is in intermediate states (connecting, reconnecting), continue polling
                setTimeout(poll, pollInterval);
            };
            // Start polling
            poll();
        });
    }
    async ensureCorrectNetwork(requestedChainId) {
        const { chainId } = ParseUtil.parseCaipNetworkId(requestedChainId);
        const networks = Object.values(chains);
        let network;
        if (requestedChainId.startsWith('eip155:')) {
            const chainIdNumber = Number(chainId);
            network = networks.find(n => n.id === chainIdNumber);
        }
        else {
            network = networks.find(n => n.caipNetworkId === requestedChainId);
        }
        if (!network) {
            throw new Error('No network found', { cause: requestedChainId });
        }
        await this.appKit?.switchNetwork(network);
        await new Promise(resolve => setTimeout(resolve, 1000));
    }
    async ensureCorrectAccount(request) {
        const address = extractAddressFromRequest(request);
        if (!address) {
            throw new Error('No address found in request');
        }
        const provider = await this.getProvider();
        const user = await provider.getUser({});
        if (user.address.toLowerCase() === address.toLowerCase()) {
            return;
        }
        if (user.preferredAccountType === 'smartAccount') {
            await provider.setPreferredAccount('eoa');
        }
        else {
            await provider.setPreferredAccount('smartAccount');
        }
    }
}
//# sourceMappingURL=AccountController.js.map